import { Grid, Theme, Typography } from '@quillbot/design-system';
import { memo } from 'react';
import {
  createBorder,
  createBorderRadius,
  cursorPointer,
  fontSize,
  fontWeight,
  grow,
  h100p,
  overflowHidden,
  primaryColor,
  unitPX,
  UNITS,
  w100p,
} from 'src/researcher/styles';
import { AddIcon } from 'src/researcher/component/icons';
import { useCreateProjectAndRoute } from 'src/researcher/hooks/useCreateProject';
import {
  closeDialogAction,
  launchDialogAction,
  useDialogDispatch,
} from 'src/researcher/context/ui/dialog';
import { ErrorCreatingProjectMsg } from 'src/researcher/constants/message.constants';
import { openToastr } from '@Common/handlers/services';
import { ActiveDialogOnPageEnum } from 'src/researcher/context/ui/dialog.types';
import { TemplateDocument } from 'src/researcher/types/api';
import researcherTrack from 'src/researcher/handlers/track';
import { CreateProjectTriggerBy } from 'src/researcher/types/common';
import { ListPageXPath } from 'src/researcher/constants/xpath.contants';

const TEMPLATE_SIZE = {
  WIDTH: UNITS['14pt'],
  HEIGHT: UNITS['18pt'],
};

// eslint-disable-next-line @typescript-eslint/naming-convention
const blank_template__add_icon_class = 'blank-template-add-icon';
// eslint-disable-next-line @typescript-eslint/naming-convention
const blank_template__root_class = 'blank-template-root';

export type TemplateCardOwnProps = {
  isLastItem: boolean;
  isBlank?: boolean;
  displayName?: string;
};

export type TemplateCardProps = TemplateDocument & TemplateCardOwnProps;

// eslint-disable-next-line @typescript-eslint/naming-convention
const border_main = createBorder(['top', 'right', 'bottom', 'left'], primaryColor);
// eslint-disable-next-line @typescript-eslint/naming-convention
const border_radius = createBorderRadius(
  ['topLeft', 'topRight', 'bottomLeft', 'bottomRight'],
  unitPX(UNITS.pt)
);

const styles = {
  spacing: { mr: unitPX(UNITS['8pt']) },
  borderRadius: border_radius,
  borderMain: border_main,
  borderDefault: createBorder(['top', 'right', 'bottom', 'left']),
  height: {
    minHeight: unitPX(TEMPLATE_SIZE.HEIGHT),
    maxHeight: unitPX(TEMPLATE_SIZE.HEIGHT),
  },
  width: {
    minWidth: unitPX(TEMPLATE_SIZE.WIDTH),
    maxWidth: unitPX(TEMPLATE_SIZE.WIDTH),
  },
  card: {
    backgroundColor: (theme: Theme) => theme.palette.grey[50],
    '&:hover': border_main,
    [`&:hover.${blank_template__root_class}`]: {
      boxShadow: (theme: Theme) => `inset 0px 0px 0px 1px ${theme.palette.primary.main}`,
    },
    [`& .${blank_template__add_icon_class}`]: {
      transition: 'all .2s ease-in-out',
    },
    [`&:hover .${blank_template__add_icon_class}`]: {
      transform: 'scale(1.5)',
    },
  },
  img: {
    height: `calc(${unitPX(TEMPLATE_SIZE.HEIGHT)} - ${unitPX(UNITS['1px'] * 2)})`,
    width: `calc(${unitPX(TEMPLATE_SIZE.WIDTH)} - ${unitPX(UNITS['1px'] * 2)})`,
  },
  descText: {
    color: (theme: Theme) => theme.specialColors.ICON_DEFAULT_COLOR,
  },
  metaInfo: { mt: unitPX(UNITS.pt) },
};

function TemplateCardComponent(props: TemplateCardProps) {
  const { isBlank = false } = props;
  const dialogDispatch = useDialogDispatch();

  const createHandler = useCreateProjectAndRoute({
    onCatch: () => {
      dialogDispatch(closeDialogAction());
      openToastr(ErrorCreatingProjectMsg);
    },
  });

  const onTemplateClick = (template: TemplateDocument) => {
    dialogDispatch(launchDialogAction(ActiveDialogOnPageEnum.CREATING_DIALOG));
    researcherTrack.createdNewProject(template.name, template.description);
    // eslint-disable-next-line no-underscore-dangle
    createHandler({ title: template.name, html: '', template: template._id });
  };

  return (
    <Grid item>
      <Grid item sx={[w100p, h100p, styles.width]}>
        <Grid
          container
          item
          justifyContent="center"
          alignItems="center"
          xs={12}
          onClick={() => {
            onTemplateClick(props);
          }}
          sx={[
            cursorPointer,
            overflowHidden,
            w100p,
            isBlank ? styles.borderMain : styles.borderDefault,
            styles.borderRadius,
            styles.height,
            styles.card,
          ]}
          className={isBlank ? blank_template__root_class : ''}
          {...ListPageXPath.TemplateCard}
        >
          {isBlank ? (
            // className={blank_template_class}
            <Grid item>
              <AddIcon sx={primaryColor} className={blank_template__add_icon_class} />
            </Grid>
          ) : (
            <img
              src={props.img}
              alt={props.name}
              style={{ ...grow, ...styles.img, ...styles.borderRadius }}
            />
          )}
        </Grid>
        <Grid
          container
          direction="column"
          item
          xs={12}
          alignItems="center"
          justifyContent="center"
          sx={styles.metaInfo}
        >
          <Typography sx={[grow, fontSize[14], fontWeight[600]]}>
            {isBlank ? props.displayName : props.name}
          </Typography>
          <Typography sx={[grow, fontSize[12], fontWeight[400], styles.descText]}>
            {props.description}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

export const TemplateCard = memo(TemplateCardComponent);
